import { type IEventHandler, type Transferable } from '@source-digital/sd-app-core'
import { type IBrokerMessenger } from './base'

export class InternalBrokerMessenger implements IBrokerMessenger {
  readonly listeners: Map<string, Map<string, IEventHandler[]>> = new Map<string, Map<string, IEventHandler[]>>()
  private readonly apis = new Map<string, unknown>()
  private readonly clientApiMap = new Map<string, Map<string, unknown>>()

  emit (eventName: string, data: Transferable, from: string, to?: string): void {
    const handlers = this.listeners.get(eventName)
    if (to != null) {
      handlers?.get(to)?.forEach(handler => { handler({ data, from, to })?.catch(console.error) })
      return
    }
    handlers?.forEach(handler => { handler.forEach(h => { h({ data, from, to })?.catch(console.error) }) })
  }

  on (eventName: string, handler: IEventHandler, receiverId: string): void {
    if (!this.listeners.has(eventName)) {
      this.listeners.set(eventName, new Map<string, IEventHandler[]>())
    }
    if (this.listeners.get(eventName)?.has(receiverId) !== true) {
      this.listeners.get(eventName)?.set(receiverId, [])
    }
    this.listeners.get(eventName)?.get(receiverId)?.push(handler)
  }

  once (eventName: string, handler: IEventHandler, receiverId: string): void {
    const onceHandler: IEventHandler = (...args: Parameters<IEventHandler>) => {
      handler(...args)?.catch(console.error)
      this.off(eventName, onceHandler, receiverId)
    }
    this.on(eventName, onceHandler, receiverId)
  }

  off (eventName: string, handler: IEventHandler, receiverId: string): void {
    const handlers = this.listeners.get(eventName)
    if (handlers?.has(receiverId) === true) {
      this.listeners.set(eventName, handlers.set(receiverId, handlers.get(receiverId)?.filter(h => h !== handler) ?? []))
    }
  }

  registerApi (apiName: string, apiDefinition: unknown): void {
    this.apis.set(apiName, apiDefinition)
  }

  unregisterApi (apiName: string): void {
    this.apis.delete(apiName)
  }

  getApi<API = unknown> (apiName: string): API {
    return this.apis.get(apiName) as API
  }

  hasClient (targetId: string): boolean {
    return this.clientApiMap.get(targetId) != null
  }

  registerClientApi (targetId: string, apiName: string, apiDefinition: unknown): void {
    if (!this.clientApiMap.has(targetId)) {
      this.clientApiMap.set(targetId, new Map<string, unknown>())
    }
    this.clientApiMap.get(targetId)?.set(apiName, apiDefinition)
  }

  unregisterClientApi (targetId: string, apiName: string): void {
    this.clientApiMap.get(targetId)?.delete(apiName)
  }

  async connectClientApi<T = any> (targetId: string, apiName: string): Promise<T> {
    const clientApi = this.clientApiMap.get(targetId)?.get(apiName)
    if (clientApi == null) {
      throw new Error(`clientApi ${targetId} ${apiName} not found`)
    }
    return await Promise.resolve(clientApi as T)
  }
}
